/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import { forwardRef } from 'react';
import { Button, ButtonProps, Tooltip } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

// Add local dependencies.
import { useStudioPermissions } from '../../hooks/useStudioPermissions';
import { StudioPermissions, StudioRole } from '../../types/studio';
import { hasRoleLevel, getRoleDisplayName } from '../../utils/permissions';

export interface RoleBasedButtonProps extends Omit<ButtonProps, 'disabled'> {
  // Permission-based access control
  permission?: keyof StudioPermissions;
  permissions?: (keyof StudioPermissions)[];
  requireAll?: boolean; // If true, requires ALL permissions; if false, requires ANY

  // Role-based access control
  role?: StudioRole;
  roles?: StudioRole[];
  requireMinimumRole?: boolean; // If true, checks role hierarchy

  // Custom permission check function
  customCheck?: (
    permissions: StudioPermissions,
    role: StudioRole | null
  ) => boolean;

  // Button behavior
  hideWhenNoAccess?: boolean; // If true, hides button instead of disabling
  showTooltipWhenDisabled?: boolean; // If true, shows tooltip explaining why disabled
  disabledTooltip?: string; // Custom tooltip message for disabled state
  showLockIcon?: boolean; // If true, shows lock icon when disabled

  // Override disabled state (useful for loading states)
  forceDisabled?: boolean;
}

/**
 * Button component that automatically handles permission-based enabling/disabling
 */
export const RoleBasedButton = forwardRef<
  HTMLButtonElement,
  RoleBasedButtonProps
>(
  (
    {
      permission,
      permissions,
      requireAll = true,
      role,
      roles,
      requireMinimumRole = false,
      customCheck,
      hideWhenNoAccess = false,
      showTooltipWhenDisabled = true,
      disabledTooltip,
      showLockIcon = false,
      forceDisabled = false,
      children,
      startIcon,
      ...buttonProps
    },
    ref
  ) => {
    const {
      permissions: userPermissions,
      role: userRole,
      hasPermission,
      isLoading,
    } = useStudioPermissions();

    // Check if user has access
    let hasAccess = true;

    // Custom permission check takes precedence
    if (customCheck) {
      hasAccess = customCheck(userPermissions, userRole);
    } else {
      // Check specific permission
      if (permission) {
        hasAccess = hasPermission(permission);
      }

      // Check multiple permissions
      if (permissions && permissions.length > 0) {
        if (requireAll) {
          // Require ALL permissions
          hasAccess = permissions.every(perm => hasPermission(perm));
        } else {
          // Require ANY permission
          hasAccess = permissions.some(perm => hasPermission(perm));
        }
      }

      // Check role-based access
      if (role) {
        if (requireMinimumRole) {
          // Check if user has minimum role level
          hasAccess = hasAccess && hasRoleLevel(userRole, role);
        } else {
          // Check exact role match
          hasAccess = hasAccess && userRole === role;
        }
      }

      // Check multiple roles
      if (roles && roles.length > 0) {
        if (requireMinimumRole) {
          // Check if user has any of the minimum role levels
          hasAccess = hasAccess && roles.some(r => hasRoleLevel(userRole, r));
        } else {
          // Check if user has any of the exact roles
          hasAccess =
            hasAccess && (userRole ? roles.includes(userRole) : false);
        }
      }
    }

    // Determine if button should be disabled
    const isDisabled = forceDisabled || isLoading || !hasAccess;

    // Hide button if no access and hideWhenNoAccess is true
    if (!hasAccess && hideWhenNoAccess) {
      return null;
    }

    // Generate tooltip message for disabled state
    const getDisabledTooltip = (): string => {
      if (disabledTooltip) {
        return disabledTooltip;
      }

      if (forceDisabled) {
        return 'This action is currently unavailable';
      }

      if (isLoading) {
        return 'Loading permissions...';
      }

      if (!hasAccess) {
        if (permission) {
          return `You need the "${permission}" permission to perform this action`;
        }
        if (permissions && permissions.length > 0) {
          const permissionList = permissions.join(', ');
          return requireAll
            ? `You need all of these permissions: ${permissionList}`
            : `You need one of these permissions: ${permissionList}`;
        }
        if (role) {
          const roleDisplayName = getRoleDisplayName(role);
          return requireMinimumRole
            ? `You need ${roleDisplayName} role or higher to perform this action`
            : `You need the ${roleDisplayName} role to perform this action`;
        }
        if (roles && roles.length > 0) {
          const roleNames = roles.map(r => getRoleDisplayName(r));
          return `You need one of these roles: ${roleNames.join(', ')}`;
        }
        return 'You do not have permission to perform this action';
      }

      return '';
    };

    // Determine start icon
    const buttonStartIcon =
      isDisabled && showLockIcon && !hasAccess ? <LockIcon /> : startIcon;

    // Create button element with essential props only
    const button = (
      <Button
        ref={ref}
        disabled={isDisabled}
        startIcon={buttonStartIcon}
        variant={buttonProps.variant}
        color={buttonProps.color}
        size={buttonProps.size}
        fullWidth={buttonProps.fullWidth}
        onClick={buttonProps.onClick}
        className={buttonProps.className}
        sx={buttonProps.sx}
        type={buttonProps.type}
        endIcon={buttonProps.endIcon}
        href={buttonProps.href}
        disableElevation={buttonProps.disableElevation}
        disableFocusRipple={buttonProps.disableFocusRipple}
        disableRipple={buttonProps.disableRipple}
        id={buttonProps.id}
      >
        {children}
      </Button>
    );

    // Wrap with tooltip if disabled and tooltip should be shown
    if (isDisabled && showTooltipWhenDisabled) {
      const tooltipMessage = getDisabledTooltip();
      if (tooltipMessage) {
        return (
          <Tooltip title={tooltipMessage} arrow>
            <span>{button}</span>
          </Tooltip>
        );
      }
    }

    return button;
  }
);

RoleBasedButton.displayName = 'RoleBasedButton';

// Note: Helper components removed to comply with ESLint no-prop-spreading rule
// Use RoleBasedButton directly with appropriate props:
// - <RoleBasedButton permission="canManageMembers">...</RoleBasedButton>
// - <RoleBasedButton role={StudioRole.OWNER}>...</RoleBasedButton>
// - <RoleBasedButton role={StudioRole.MANAGER} requireMinimumRole>...</RoleBasedButton>
